import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert, Button, Nav, Tab } from 'react-bootstrap';
import { useAuth } from "react-oidc-context";
import { useUser } from "../context/UserContext";
import { userManagementAPI } from '../utils/apiClient';
import BacklogTable from '../components/BacklogTable';
import ExecutionLogTable from '../components/ExecutionLogTable';
import BacklogJobErrorsTable from '../components/BacklogJobErrorsTable';
import AccessErrorTable from '../components/AccessErrorTable';
import LoadingSpinner from '../components/LoadingSpinner';
import '../components/Dropdowns.css';
import { useUserEmailCache } from '../hooks/useUserEmailCache';

function Reporting() {
  useEffect(() => {
    document.title = 'Reporting - LostLogic';
  }, []);

  const getUpcomingSaturday = () => {
    const today = new Date();
    const nextSaturday = new Date();
  
    // Calculate days until Saturday (6)
    const day = today.getDay(); // 0 = Sun ... 6 = Sat
    const diff = (6 - day + 7) % 7 || 7; 
    nextSaturday.setDate(today.getDate() + diff);
  
    // Format DD-MM-YYYY
    const dd = String(nextSaturday.getDate()).padStart(2, "0");
    const mm = String(nextSaturday.getMonth() + 1).padStart(2, "0");
    const yyyy = nextSaturday.getFullYear();
  
    return `${dd}-${mm}-${yyyy}`;
  };
  

  const auth = useAuth();
  const { orgHash } = useUser();
  
  // Active tab
  const [activeTab, setActiveTab] = useState('backlog');

  // get hash values from API
  const { getUserEmail, isLoading: userCacheLoading } = useUserEmailCache();



  
  // Backlog data
  const [backlogData, setBacklogData] = useState([]);
  const [loadingBacklog, setLoadingBacklog] = useState(true);
  const [backlogError, setBacklogError] = useState(null);
  
  // Execution log data
  const [executionLogData, setExecutionLogData] = useState([]);
  const [loadingExecutionLog, setLoadingExecutionLog] = useState(false);
  const [executionLogError, setExecutionLogError] = useState(null);
  
  // User management errors data
  const [mkrmErrorData, setMkrmErrorData] = useState([]);
  const [loadingMkrmErrors, setLoadingMkrmErrors] = useState(false);
  const [mkrmErrorsError, setMkrmErrorsError] = useState(null);
  
  // Access errors data
  const [accessErrorData, setAccessErrorData] = useState([]);
  const [loadingAccessErrors, setLoadingAccessErrors] = useState(false);
  const [accessErrorsError, setAccessErrorsError] = useState(null);
  
  // Track if initial load is complete
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Auto-refresh interval (every 10 seconds for backlog only)
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Combined loading state
  const isAnyLoading = loadingBacklog || loadingExecutionLog || loadingMkrmErrors || loadingAccessErrors;
  
  // Fetch backlog data
  const fetchBacklog = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setBacklogError(null);
      const response = await userManagementAPI.getPendingActions(token, orgHash);
      setBacklogData(response.data || []);
    } catch (err) {
      console.error('Error fetching backlog:', err);
      setBacklogError('Failed to load backlog: ' + err.message);
    } finally {
      setLoadingBacklog(false);
    }
  };
  
  // Fetch execution log
  const fetchExecutionLog = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setExecutionLogError(null);
      setLoadingExecutionLog(true);
      const response = await userManagementAPI.getExecutionLog(token, orgHash);
      setExecutionLogData(response.data || []);
      console.log(`Loaded ${response.data?.length || 0} execution log entries`);
    } catch (err) {
      console.error('Error fetching execution log:', err);
      setExecutionLogError('Failed to load execution log: ' + err.message);
    } finally {
      setLoadingExecutionLog(false);
    }
  };
  
  // Fetch user management errors
  const fetchMkrmErrors = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setMkrmErrorsError(null);
      setLoadingMkrmErrors(true);
      const response = await userManagementAPI.getMkrmErrors(token, orgHash);
      setMkrmErrorData(response.data || []);
      console.log(`Loaded ${response.data?.length || 0} user management errors`);
    } catch (err) {
      console.error('Error fetching mkrm errors:', err);
      setMkrmErrorsError('Failed to load errors: ' + err.message);
    } finally {
      setLoadingMkrmErrors(false);
    }
  };
  
  // Fetch access errors
  const fetchAccessErrors = async () => {
    const token = auth.user?.access_token;
    if (!token || !orgHash) return;
    
    try {
      setAccessErrorsError(null);
      setLoadingAccessErrors(true);
      
      // Date is automatically determined by Lambda (latest from dates.csv)
      const response = await userManagementAPI.getAccessErrors(token, orgHash);
      setAccessErrorData(response.data || []);
      console.log(`Loaded ${response.data?.length || 0} access errors (date: ${response.date})`);
    } catch (err) {
      console.error('Error fetching access errors:', err);
      setAccessErrorsError('Failed to load access errors: ' + err.message);
    } finally {
      setLoadingAccessErrors(false);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    if (orgHash) {
      // Fetch all data in parallel
      Promise.all([
        fetchBacklog(),
        fetchExecutionLog(),
        fetchMkrmErrors(),
        fetchAccessErrors()
      ]).then(() => {
        setHasLoadedOnce(true);
      });
    }
  }, [orgHash]);
  
  // Manual refresh
  const handleRefresh = () => {
    setLoadingBacklog(true);
    setLoadingExecutionLog(true);
    setLoadingMkrmErrors(true);
    setLoadingAccessErrors(true);
    fetchBacklog();
    fetchExecutionLog();
    fetchMkrmErrors();
    fetchAccessErrors();
  };
  
  // Handle delete from backlog
  const handleDeleteFromBacklog = async (org, emailProperty) => {
    const token = auth.user?.access_token;
    if (!token) return;
    
    try {
      await userManagementAPI.removePendingAction(token, org, emailProperty);
      // Refresh backlog after deletion
      fetchBacklog();
    } catch (err) {
      console.error('Error deleting from backlog:', err);
      alert('Failed to delete record: ' + err.message);
    }
  };
  
  // Show full-page spinner on initial load
  if (isAnyLoading && !hasLoadedOnce) {
    return (
      <Container fluid className="mt-4">
        <Row className="mb-3">
          <Col>
            <h2>User Management Log</h2>
          </Col>
        </Row>
        <Row className="mt-5">
          <Col className="text-center">
            <LoadingSpinner message="Loading reporting data..." />
          </Col>
        </Row>
      </Container>
    );
  }
  
  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <h2>User Management Log</h2>
        </Col>
        <Col xs="auto">
          <Button 
            variant="outline-primary" 
            onClick={handleRefresh}
            disabled={isAnyLoading}
          >
            <i className="fas fa-sync-alt"></i> Refresh
          </Button>
          {' '}
          <Button 
            variant={autoRefresh ? "success" : "outline-secondary"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="sm"
          >
            Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </Col>
      </Row>
      
      {/* Tabbed Interface */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Row>
          <Col>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item id="reporting-nav">
                <Nav.Link eventKey="backlog">
                  Backlog &nbsp;
                  {backlogData.length > 0 && (
                    <span className="badge bg-primary ms-2" Style="color:#ffffff">{backlogData.length}</span>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item id="reporting-nav2">
                <Nav.Link eventKey="log">
                  Log &nbsp;
                  {executionLogData.length > 0 && (
                    <span className="badge bg-secondary ms-2">{executionLogData.length}</span>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item id="reporting-nav3">
                <Nav.Link eventKey="errors">
                  Errors &nbsp;
                  {(mkrmErrorData.length > 0 || accessErrorData.length > 0) && (
                    <span className="badge ms-2" Style="background-color: #e74c3c; color:#ffffff">
                      {mkrmErrorData.length + accessErrorData.length}
                    </span>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>
            
            <Tab.Content>
              {/* Backlog Tab */}
              <Tab.Pane eventKey="backlog">
                <h4 className="mb-3">Pending Actions</h4>
                <span Style="color:var(--dark)">
                  The jobs listed below will be on Saturday,  {getUpcomingSaturday()}.
                  <br/>
                  You can cancel any single job by removing them from the queue listed below.
                </span>
                
                {backlogError && (
                  <Alert variant="danger">{backlogError}</Alert>
                )}
                
                {/* Show inline spinner in backlog tab when loading */}
                {loadingBacklog && hasLoadedOnce && (
                  <Row className="mt-5">
                    <Col className="text-center">
                      <LoadingSpinner message="Refreshing backlog..." />
                    </Col>
                  </Row>
                )}
                
                {!loadingBacklog && (
                  <BacklogTable 
                    data={backlogData}
                    isLoading={false}
                    onDelete={handleDeleteFromBacklog}
                    getUserEmail={getUserEmail}
                  />
                )}
              </Tab.Pane>
              
              {/* Log Tab */}
              <Tab.Pane eventKey="log">
                <h4 className="mb-3">Execution Log</h4>
                
                {executionLogError && (
                  <Alert variant="danger">{executionLogError}</Alert>
                )}
                
                {/* Show inline spinner in log tab when loading */}
                {loadingExecutionLog && hasLoadedOnce && (
                  <Row className="mt-5">
                    <Col className="text-center">
                      <LoadingSpinner message="Refreshing execution log..." />
                    </Col>
                  </Row>
                )}
                
                {!loadingExecutionLog && (
                  <ExecutionLogTable 
                    data={executionLogData}
                    isLoading={false}
                    getUserEmail={getUserEmail}
                  />
                )}
              </Tab.Pane>
              
              {/* Errors Tab */}
              <Tab.Pane eventKey="errors">
                <Row>
                  <Col>
                    <h4 className="mb-3">User Management Errors</h4>
                    
                    {mkrmErrorsError && (
                      <Alert variant="danger">{mkrmErrorsError}</Alert>
                    )}
                    
                    {/* Show inline spinner when loading */}
                    {loadingMkrmErrors && hasLoadedOnce && (
                      <Row className="mt-5">
                        <Col className="text-center">
                          <LoadingSpinner message="Refreshing errors..." />
                        </Col>
                      </Row>
                    )}
                    
                    {!loadingMkrmErrors && (
                      <BacklogJobErrorsTable 
                        data={mkrmErrorData}
                        isLoading={false}
                        getUserEmail={getUserEmail}
                      />
                    )}
                  </Col>
                </Row>
                
                <Row className="mt-5">
                  <Col>
                    <h4 className="mb-3">Access Errors</h4>
                    
                    {accessErrorsError && (
                      <Alert variant="danger">{accessErrorsError}</Alert>
                    )}
                    
                    {/* Show inline spinner when loading */}
                    {loadingAccessErrors && hasLoadedOnce && (
                      <Row className="mt-5">
                        <Col className="text-center">
                          <LoadingSpinner message="Refreshing access errors..." />
                        </Col>
                      </Row>
                    )}
                    
                    {!loadingAccessErrors && (
                      <AccessErrorTable 
                        data={accessErrorData}
                        isLoading={false}
                      />
                    )}
                  </Col>
                </Row>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
}

export default Reporting;















// import React, { useState, useEffect } from 'react';
// import { Container, Row, Col, Spinner, Alert, Button, Nav, Tab } from 'react-bootstrap';
// import { useAuth } from "react-oidc-context";
// import { useUser } from "../context/UserContext";
// import { userManagementAPI } from '../utils/apiClient';
// import BacklogTable from '../components/BacklogTable';
// import ExecutionLogTable from '../components/ExecutionLogTable';
// import BacklogJobErrorsTable from '../components/BacklogJobErrorsTable';
// import AccessErrorTable from '../components/AccessErrorTable';
// import '../components/Dropdowns.css';

// function Reporting() {
//   useEffect(() => {
//     document.title = 'Reporting - LostLogic';
//   }, []);

//   const getUpcomingSaturday = () => {
//     const today = new Date();
//     const nextSaturday = new Date();
  
//     // Calculate days until Saturday (6)
//     const day = today.getDay(); // 0 = Sun ... 6 = Sat
//     const diff = (6 - day + 7) % 7 || 7; 
//     nextSaturday.setDate(today.getDate() + diff);
  
//     // Format DD-MM-YYYY
//     const dd = String(nextSaturday.getDate()).padStart(2, "0");
//     const mm = String(nextSaturday.getMonth() + 1).padStart(2, "0");
//     const yyyy = nextSaturday.getFullYear();
  
//     return `${dd}-${mm}-${yyyy}`;
//   };
  

//   const auth = useAuth();
//   const { orgHash } = useUser();
  
//   // Active tab
//   const [activeTab, setActiveTab] = useState('backlog');
  
//   // Backlog data
//   const [backlogData, setBacklogData] = useState([]);
//   const [loadingBacklog, setLoadingBacklog] = useState(true);
//   const [backlogError, setBacklogError] = useState(null);
  
//   // Execution log data
//   const [executionLogData, setExecutionLogData] = useState([]);
//   const [loadingExecutionLog, setLoadingExecutionLog] = useState(false);
//   const [executionLogError, setExecutionLogError] = useState(null);
  
//   // User management errors data
//   const [mkrmErrorData, setMkrmErrorData] = useState([]);
//   const [loadingMkrmErrors, setLoadingMkrmErrors] = useState(false);
//   const [mkrmErrorsError, setMkrmErrorsError] = useState(null);
  
//   // Access errors data
//   const [accessErrorData, setAccessErrorData] = useState([]);
//   const [loadingAccessErrors, setLoadingAccessErrors] = useState(false);
//   const [accessErrorsError, setAccessErrorsError] = useState(null);
  
//   // Auto-refresh interval (every 10 seconds for backlog only)
//   const [autoRefresh, setAutoRefresh] = useState(true);
  
//   // Fetch backlog data
//   const fetchBacklog = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setBacklogError(null);
//       const response = await userManagementAPI.getPendingActions(token, orgHash);
//       setBacklogData(response.data || []);
//     } catch (err) {
//       console.error('Error fetching backlog:', err);
//       setBacklogError('Failed to load backlog: ' + err.message);
//     } finally {
//       setLoadingBacklog(false);
//     }
//   };
  
//   // Fetch execution log
//   const fetchExecutionLog = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setExecutionLogError(null);
//       setLoadingExecutionLog(true);
//       const response = await userManagementAPI.getExecutionLog(token, orgHash);
//       setExecutionLogData(response.data || []);
//       console.log(`Loaded ${response.data?.length || 0} execution log entries`);
//     } catch (err) {
//       console.error('Error fetching execution log:', err);
//       setExecutionLogError('Failed to load execution log: ' + err.message);
//     } finally {
//       setLoadingExecutionLog(false);
//     }
//   };
  
//   // Fetch user management errors
//   const fetchMkrmErrors = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setMkrmErrorsError(null);
//       setLoadingMkrmErrors(true);
//       const response = await userManagementAPI.getMkrmErrors(token, orgHash);
//       setMkrmErrorData(response.data || []);
//       console.log(`Loaded ${response.data?.length || 0} user management errors`);
//     } catch (err) {
//       console.error('Error fetching mkrm errors:', err);
//       setMkrmErrorsError('Failed to load errors: ' + err.message);
//     } finally {
//       setLoadingMkrmErrors(false);
//     }
//   };
  
//   // Fetch access errors
//   const fetchAccessErrors = async () => {
//     const token = auth.user?.access_token;
//     if (!token || !orgHash) return;
    
//     try {
//       setAccessErrorsError(null);
//       setLoadingAccessErrors(true);
      
//       // Date is automatically determined by Lambda (latest from dates.csv)
//       const response = await userManagementAPI.getAccessErrors(token, orgHash);
//       setAccessErrorData(response.data || []);
//       console.log(`Loaded ${response.data?.length || 0} access errors (date: ${response.date})`);
//     } catch (err) {
//       console.error('Error fetching access errors:', err);
//       setAccessErrorsError('Failed to load access errors: ' + err.message);
//     } finally {
//       setLoadingAccessErrors(false);
//     }
//   };
  
//   // Initial fetch
//   useEffect(() => {
//     if (orgHash) {
//       fetchBacklog();
//       fetchExecutionLog();
//       fetchMkrmErrors();
//       fetchAccessErrors();
//     }
//   }, [orgHash]);
  
//   // Auto-refresh backlog every 10 seconds
//   // useEffect(() => {
//   //   if (!autoRefresh) return;
    
//   //   const interval = setInterval(() => {
//   //     fetchBacklog();
//   //   }, 10000); // 10 seconds
    
//   //   return () => clearInterval(interval);
//   // }, [autoRefresh, orgHash]);
  
//   // Manual refresh
//   const handleRefresh = () => {
//     setLoadingBacklog(true);
//     setLoadingExecutionLog(true);
//     setLoadingMkrmErrors(true);
//     setLoadingAccessErrors(true);
//     fetchBacklog();
//     fetchExecutionLog();
//     fetchMkrmErrors();
//     fetchAccessErrors();
//   };
  
//   // Handle delete from backlog
//   const handleDeleteFromBacklog = async (org, emailProperty) => {
//     const token = auth.user?.access_token;
//     if (!token) return;
    
//     try {
//       await userManagementAPI.removePendingAction(token, org, emailProperty);
//       // Refresh backlog after deletion
//       fetchBacklog();
//     } catch (err) {
//       console.error('Error deleting from backlog:', err);
//       alert('Failed to delete record: ' + err.message);
//     }
//   };
  
//   return (
//     <Container fluid className="mt-4">
//       {/* Header */}
//       <Row className="mb-3">
//         <Col>
//           <h2>User Management Log</h2>
//         </Col>
//         <Col xs="auto">
//           <Button 
//             variant="outline-primary" 
//             onClick={handleRefresh}
//             disabled={loadingBacklog}
//           >
//             <i className="fas fa-sync-alt"></i> Refresh
//           </Button>
//           {' '}
//           <Button 
//             variant={autoRefresh ? "success" : "outline-secondary"}
//             onClick={() => setAutoRefresh(!autoRefresh)}
//             size="sm"
//           >
//             Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
//           </Button>
//         </Col>
//       </Row>
      
//       {/* Tabbed Interface */}
//       <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
//         <Row>
//           <Col>
//             <Nav variant="tabs" className="mb-3">
//               <Nav.Item>
//                 <Nav.Link eventKey="backlog">
//                   Backlog &nbsp;
//                   {backlogData.length > 0 && (
//                     <span className="badge bg-primary ms-2" Style="color:#ffffff">{backlogData.length}</span>
//                   )}
//                 </Nav.Link>
//               </Nav.Item>
//               <Nav.Item>
//                 <Nav.Link eventKey="log">
//                   Log &nbsp;
//                   {executionLogData.length > 0 && (
//                     <span className="badge bg-secondary ms-2">{executionLogData.length}</span>
//                   )}
//                 </Nav.Link>
//               </Nav.Item>
//               <Nav.Item>
//                 <Nav.Link eventKey="errors">
//                   Errors &nbsp;
//                   {(mkrmErrorData.length > 0 || accessErrorData.length > 0) && (
//                     <span className="badge ms-2" Style="background-color: #e74c3c; color:#ffffff">
//                       {mkrmErrorData.length + accessErrorData.length}
//                     </span>
//                   )}
//                 </Nav.Link>
//               </Nav.Item>
//             </Nav>
            
//             <Tab.Content>
//               {/* Backlog Tab */}
//               <Tab.Pane eventKey="backlog">
//                 <h4 className="mb-3">
//                   Pending Actions 
//                   {loadingBacklog && <Spinner animation="border" size="sm" className="ms-2" />}
//                 </h4>
//                 {/* <span Style="color:#191919"> */}
//                 <span Style="color:var(--dark)">
//                   The jobs listed below will be on Saturday,  {getUpcomingSaturday()}.
//                   <br/>
//                   You can cancel any single job by removing them from the queue listed below.
//                 </span>
                
//                 {backlogError && (
//                   <Alert variant="danger">{backlogError}</Alert>
//                 )}
                
//                 <BacklogTable 
//                   data={backlogData}
//                   isLoading={loadingBacklog}
//                   onDelete={handleDeleteFromBacklog}
//                 />
//               </Tab.Pane>
              
//               {/* Log Tab */}
//               <Tab.Pane eventKey="log">
//                 <h4 className="mb-3">
//                   Execution Log
//                   {loadingExecutionLog && <Spinner animation="border" size="sm" className="ms-2" />}
//                 </h4>
                
//                 {executionLogError && (
//                   <Alert variant="danger">{executionLogError}</Alert>
//                 )}
                
//                 <ExecutionLogTable 
//                   data={executionLogData}
//                   isLoading={loadingExecutionLog}
//                 />
//               </Tab.Pane>
              
//               {/* Errors Tab */}
//               <Tab.Pane eventKey="errors">
//                 <Row>
//                   <Col>
//                     <h4 className="mb-3">
//                       User Management Errors
//                       {loadingMkrmErrors && <Spinner animation="border" size="sm" className="ms-2" />}
//                     </h4>
                    
//                     {mkrmErrorsError && (
//                       <Alert variant="danger">{mkrmErrorsError}</Alert>
//                     )}
                    
//                     <BacklogJobErrorsTable 
//                       data={mkrmErrorData}
//                       isLoading={loadingMkrmErrors}
//                     />
//                   </Col>
//                 </Row>
                
//                 <Row className="mt-5">
//                   <Col>
//                     <h4 className="mb-3">
//                       Access Errors
//                       {loadingAccessErrors && <Spinner animation="border" size="sm" className="ms-2" />}
//                     </h4>
                    
//                     {accessErrorsError && (
//                       <Alert variant="danger">{accessErrorsError}</Alert>
//                     )}
                    
//                     <AccessErrorTable 
//                       data={accessErrorData}
//                       isLoading={loadingAccessErrors}
//                     />
//                   </Col>
//                 </Row>
//               </Tab.Pane>
//             </Tab.Content>
//           </Col>
//         </Row>
//       </Tab.Container>
//     </Container>
//   );
// }
// // export default UserManagementLog;
// export default Reporting;